import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { LogOut, Eye, X } from "lucide-react";
import { toast } from "sonner";

import AutheraLogo from "@/components/AutheraLogo";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

const RoleTab = ({ to, label, active, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all ${
      active
        ? "bg-white text-[#0055FF] shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
        : "text-gray-600 hover:text-gray-900"
    }`}
  >
    {label}
  </Link>
);

export default function Nav() {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  const { user, logout, switchRetailer, exitImpersonation } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const handleExit = async () => {
    try {
      await exitImpersonation();
      toast.success("Restored admin session");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not exit impersonation");
    }
  };

  return (
    <>
      {user?.impersonating && (
        <div
          data-testid="impersonation-banner"
          className="bg-amber-50 border-b border-amber-200 text-amber-900"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-3 text-xs">
            <div className="inline-flex items-center gap-2 font-semibold">
              <Eye size={13} />
              <span>
                Viewing as{" "}
                <span className="font-bold">{user.retailer_name}</span>{" "}
                <span className="font-mono text-[10px] text-amber-700">({user.retailer_id})</span>
              </span>
            </div>
            <button
              onClick={handleExit}
              data-testid="impersonation-exit"
              className="inline-flex items-center gap-1.5 text-[11px] font-bold text-white bg-amber-600 hover:bg-amber-700 px-2.5 py-1 rounded-md transition"
            >
              <X size={11} />
              Exit → Admin
            </button>
          </div>
        </div>
      )}
      <header
        data-testid="top-nav"
        className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-gray-200"
      >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <button
          onClick={() => navigate("/")}
          data-testid="brand-logo"
          className="flex items-center gap-2.5 group"
        >
          <span className="group-hover:scale-105 transition-transform">
            <AutheraLogo size="sm" testid="nav-brand-logo" />
          </span>
          <span className="font-display font-bold text-lg tracking-tight text-gray-900">
            Authera
            <span className="text-[#0055FF]">.</span>
          </span>
          <span className="hidden sm:inline-block text-[10px] font-medium tracking-[0.15em] uppercase text-gray-400 border-l border-gray-200 pl-2.5 ml-1">
            Digital Asset Verification
          </span>
        </button>

        <nav
          data-testid="role-switcher"
          className="flex items-center gap-1 bg-gray-100/80 p-1 rounded-lg border border-gray-200"
        >
          <RoleTab
            to="/retailer"
            label="Retailer"
            active={path.startsWith("/retailer")}
            testid="nav-retailer"
          />
          <RoleTab
            to="/customer"
            label="Customer"
            active={path.startsWith("/customer")}
            testid="nav-customer"
          />
          <RoleTab
            to="/verify"
            label="Verify"
            active={path === "/verify" || path.startsWith("/verify/") || path.startsWith("/verification")}
            testid="nav-verify"
          />
        </nav>

        <div className="hidden sm:flex items-center gap-3">
          {user?.role === "admin" && !user?.impersonating && (
            <ViewAsDropdown onSwitch={switchRetailer} />
          )}
          {user ? (
            <>
              <div
                data-testid="nav-session-context"
                className="inline-flex items-center gap-1 text-[11px] text-gray-600 pr-1"
                title={user.role === "admin" ? "Admin session" : `Retailer ID: ${user.retailer_id}`}
              >
                <span className="text-gray-400">Logged in as</span>
                {" "}
                <span className="font-semibold text-gray-900">
                  {user.role === "admin"
                    ? "Admin"
                    : `${user.retailer_name} (${user.retailer_id})`}
                </span>
              </div>
              <button
                onClick={handleLogout}
                data-testid="nav-logout"
                title={user.role === "admin" ? `Sign out ${user.email}` : `Sign out of ${user.retailer_name}`}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700 hover:text-[#0055FF] border border-gray-200 hover:border-[#0055FF] px-3 py-1.5 rounded-md transition"
              >
                <LogOut size={13} />
                Sign out
              </button>
            </>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700 hover:text-[#0055FF] border border-gray-200 hover:border-[#0055FF] px-3 py-1.5 rounded-md transition"
            >
              Sign in
            </Link>
          )}
          <span className="text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-500">
            Authera
          </span>
        </div>
      </div>
    </header>
    </>
  );
}


/**
 * Admin-only "View as" dropdown — fetches retailers on first open and lets
 * the admin enter any retailer's dashboard view via the impersonation API.
 */
function ViewAsDropdown({ onSwitch }) {
  const [open, setOpen] = useState(false);
  const [retailers, setRetailers] = useState(null); // null = unloaded
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open || retailers !== null) return;
    api
      .get("/admin/retailers")
      .then((r) => setRetailers(r.data?.rows || []))
      .catch(() => setRetailers([]));
  }, [open, retailers]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const onPick = async (rid) => {
    if (busy) return;
    setBusy(true);
    try {
      await onSwitch(rid);
      setOpen(false);
      toast.success(`Now viewing as ${rid}`);
      // Hard reload so all panels re-fetch with the new tenant context.
      // (auth context state alone is enough, but a reload guarantees no
      // tab carries cached admin-only data into the impersonation view.)
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Switch failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative" ref={ref} data-testid="view-as-dropdown-wrap">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="view-as-trigger"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700 hover:text-[#0055FF] border border-gray-200 hover:border-[#0055FF] px-3 py-1.5 rounded-md transition"
      >
        <Eye size={13} />
        View as Retailer
      </button>
      {open && (
        <div
          data-testid="view-as-menu"
          className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-[0_12px_32px_rgba(0,0,0,0.12)] overflow-hidden z-50"
        >
          <div className="px-4 py-2 text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-400 border-b border-gray-100">
            Switch retailer
          </div>
          {retailers === null ? (
            <div className="px-4 py-3 text-xs text-gray-500">Loading retailers…</div>
          ) : retailers.length === 0 ? (
            <div className="px-4 py-3 text-xs text-gray-500">No retailers</div>
          ) : (
            <ul className="max-h-72 overflow-y-auto">
              {retailers.map((r) => (
                <li key={r.retailer_id}>
                  <button
                    type="button"
                    disabled={busy || !r.active}
                    onClick={() => onPick(r.retailer_id)}
                    data-testid={`view-as-${r.retailer_id}`}
                    className="w-full text-left px-4 py-2.5 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <div className="text-sm font-semibold text-gray-900">
                      {r.name}
                    </div>
                    <div className="text-[11px] font-mono text-gray-500">
                      {r.retailer_id}
                      {!r.active ? " · inactive" : ""}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
